import { Link } from 'react-router-dom';
import { Download, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import PageShell from '@/components/PageShell';
import Logo from '@/components/Logo';

/* ================================================================
   /brand — The Mobility Group · finalised brand mark
   Star mark + stacked wordmark, all-amber colourway.
================================================================ */

// Asset list — everything downloadable
const HORIZONTAL_LOCKUPS = [
  ['/tmg-lockup-horizontal-dark.png', 'On black · PNG', '1200×300'],
  ['/tmg-lockup-horizontal-light.png', 'On cream · PNG', '1200×300'],
  ['/tmg-lockup-horizontal-transparent-white.png', 'Transparent · white text', '1200×300'],
  ['/tmg-lockup-horizontal-transparent-dark.png', 'Transparent · dark text', '1200×300'],
  ['/brand/tmg-lockup-horizontal-dark.svg', 'On black · SVG', 'vector'],
  ['/brand/tmg-lockup-horizontal-light.svg', 'On cream · SVG', 'vector'],
];

const STAR_ASSETS = [
  ['/brand/tmg-star-linkedin-dark-1024.png', 'LinkedIn · amber on black · 1024', 'PNG · square · safe area OK'],
  ['/brand/tmg-star-linkedin-white-1024.png', 'Amber on white · 1024', 'PNG · square'],
  ['/brand/tmg-star-linkedin-light-1024.png', 'Amber on cream · 1024', 'PNG · square'],
  ['/brand/tmg-star-linkedin-transparent-1024.png', 'Amber · transparent · 1024', 'PNG · square'],
  ['/brand/tmg-star-linkedin-white-mark-1024.png', 'White on black · 1024', 'PNG · square'],
  ['/brand/tmg-star-linkedin-dark-400.png', 'LinkedIn · amber on black · 400', 'PNG · square (LinkedIn min)'],
  ['/brand/tmg-star-dark.svg', 'Amber on black · SVG', 'vector'],
  ['/brand/tmg-star-transparent.svg', 'Amber transparent · SVG', 'vector'],
  ['/brand/tmg-star-white.svg', 'White on black · SVG', 'vector'],
  ['/brand/tmg-star-amber.pdf', 'Amber · PDF', 'print / vector'],
  ['/brand/tmg-star-white.pdf', 'White · PDF', 'print / vector'],
];

const AssetTile = ({ href, label, meta }) => (
  <a
    href={href}
    download=""
    data-testid={`asset-${href.replace(/[^a-zA-Z0-9]+/g, '-')}`}
    className="group flex items-center justify-between gap-4 bg-ink-900 border border-white/10 hover:border-amber p-5 transition-colors"
  >
    <div className="min-w-0">
      <div className="font-display text-base text-white group-hover:text-amber transition-colors truncate">
        {label}
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        {meta}
      </div>
    </div>
    <Download className="w-4 h-4 text-zinc-500 group-hover:text-amber transition-colors flex-shrink-0" />
  </a>
);

const BrandLogos = () => {
  return (
    <PageShell>
      {/* HERO — the finalised mark, big */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute inset-0 radial-glow-amber opacity-25" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Brand · finalised
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance max-w-4xl leading-[0.98]">
            The mark, <span className="text-amber">locked in.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
            8-point star mark + stacked THE · MOBILITY · GROUP wordmark, in
            amber. Live across the site header, footer, PDFs and social
            previews. Download the pack below.
          </p>

          {/* Big canonical render */}
          <div className="mt-16 flex items-center justify-center bg-ink-900 border border-white/10 py-16 md:py-24">
            <Logo variant="amber" size={200} testId="brand-hero-logo" />
          </div>

          {/* What the mark stands for — narrative */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="overline text-amber mb-3">What it stands for</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight text-balance">
                A group, drawn as a
                <br />
                <span className="text-amber">constellation.</span>
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-4 text-zinc-300 text-[15px] md:text-base leading-relaxed">
              <p>
                Every ray is a founder. Every ray is a venture — an automotive
                business built, scaled and still run by the same person, in
                India, for more than a decade.
              </p>
              <p>
                The rays converge at a single point:{' '}
                <span className="text-amber">mobility</span> infrastructure,{' '}
                <span className="text-amber">data</span>,{' '}
                <span className="text-amber">capital</span>, and each other.
                That&apos;s what the group is — and why the whole is worth more
                than its ventures.
              </p>
              <p className="text-zinc-400">
                New founders bring new rays. The constellation widens with
                time. The centre only gets brighter.
              </p>
            </div>
          </div>

          {/* Alternate colour + variants */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-black border border-white/10 py-10 flex flex-col items-center justify-center gap-3 min-h-[180px]">
              <Logo variant="amber" size={100} />
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber/80">
                Amber · on black
              </div>
            </div>
            <div className="bg-white border border-black/10 py-10 flex flex-col items-center justify-center gap-3 min-h-[180px]">
              <Logo variant="amber" size={100} />
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/60">
                Amber · on white
              </div>
            </div>
            <div className="bg-black border border-white/10 py-10 flex flex-col items-center justify-center gap-3 min-h-[180px]">
              <Logo variant="white" size={100} />
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60">
                White · on black
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USAGE */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline text-amber mb-3">Usage · quick rules</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Keep it precious.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              ['Colour', 'Amber #F59E0B is the default. White is only for very dark surfaces. Do not use any other colour.'],
              ['Clear space', 'Minimum clear space around the mark = the height of one word in the wordmark.'],
              ['Backgrounds', 'Prefer plain solid dark (ink #0A0A0A) or plain white (#FFFFFF). Avoid busy photos without a tint.'],
              ['Do not', 'Do not distort, recolour, add drop-shadows, outlines, gradients, or rotate. Do not use the star and wordmark separately in the same layout.'],
            ].map(([title, body]) => (
              <div key={title} className="bg-ink-800 border border-white/10 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-amber" />
                  <div className="font-display text-base text-white">{title}</div>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LINKEDIN BANNER · preview + download */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="overline text-amber mb-3">LinkedIn banner</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Cover image for the company page.
            </h2>
            <p className="mt-3 text-zinc-400 text-sm md:text-base max-w-2xl leading-relaxed">
              1128 × 191 (LinkedIn spec), exported at 2× for retina crispness.
              The constellation option below is our recommendation — data
              ecosystem language on the right, the finalised lockup on the
              left.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a
              href="/brand/tmg-linkedin-banner-01-constellation.png"
              download=""
              data-testid="brand-download-banner-constellation"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download PNG
            </a>
            <Link
              to="/brand/banner"
              data-testid="brand-view-all-banners"
              className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-amber hover:text-amber transition-colors"
            >
              See all 5 options
            </Link>
          </div>
        </div>

        {/* Preview at true LinkedIn aspect ratio */}
        <div className="border border-white/10 overflow-hidden bg-black">
          <img
            src="/brand/tmg-linkedin-banner-01-constellation.png?v=9"
            alt="The Mobility Group — LinkedIn banner · Constellation"
            className="w-full h-auto block"
            data-testid="brand-linkedin-banner-preview"
          />
        </div>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Option 01 · Constellation network · 1128 × 191 · 2×
        </div>
      </section>

      {/* DOWNLOADS · full pack */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 border-b border-white/10 bg-ink-900/30">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-10">
          <div className="lg:col-span-8">
            <div className="overline text-amber mb-3">Brand pack</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Download the entire pack in one ZIP.
            </h2>
            <p className="mt-3 text-zinc-400 text-sm md:text-base max-w-2xl leading-relaxed">
              Horizontal lockups (PNG, JPG, editable SVG), square star mark
              (PNG @ 400 & 1024, SVG, PDF), five LinkedIn banner options,
              legacy monogram, and a short usage README.
              Roughly 300&nbsp;KB.
            </p>
          </div>
          <div className="lg:col-span-4 flex justify-start lg:justify-end">
            <a
              href="/tmg-brand-assets.zip"
              download=""
              data-testid="brand-download-zip"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download ZIP
            </a>
          </div>
        </div>
      </section>

      {/* DOWNLOADS · star (LinkedIn) */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 border-b border-white/10">
        <div className="mb-10">
          <div className="overline text-amber mb-3">Star mark · square</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            For LinkedIn, avatars, favicons.
          </h2>
          <p className="mt-3 text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed">
            The star alone as a square asset. Sized for LinkedIn company
            avatars (400 min / 1024 hi-res), respects LinkedIn&apos;s circular
            crop safe area, all 8 rays fit inside with margin.{' '}
            <span className="text-amber">Amber PDF</span> and{' '}
            <span className="text-amber">white PDF</span> included for print.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STAR_ASSETS.map(([href, label, meta]) => (
            <AssetTile key={href} href={href} label={label} meta={meta} />
          ))}
        </div>
      </section>

      {/* DOWNLOADS · horizontal lockups */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 border-b border-white/10">
        <div className="mb-10">
          <div className="overline text-amber mb-3">Horizontal lockup</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            For headers, letterheads, email signatures.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {HORIZONTAL_LOCKUPS.map(([href, label, meta]) => (
            <AssetTile key={href} href={href} label={label} meta={meta} />
          ))}
        </div>
      </section>

      {/* Bottom links */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <div className="flex flex-wrap gap-4">
          <Link
            to="/brand/banner"
            data-testid="brand-view-banner"
            className="inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
          >
            LinkedIn banner options
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/brand/v2"
            data-testid="brand-view-explorations"
            className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-amber hover:text-amber transition-colors"
          >
            View earlier explorations
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/links"
            data-testid="brand-view-links"
            className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-white hover:border-amber hover:text-amber transition-colors"
          >
            All site links
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default BrandLogos;
